import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Pagination} from 'antd';
import dayjs from 'dayjs';
import {useTranslation} from 'react-i18next';
import {useWarehouseContext} from '../../../contexts/WarehouseContext';
import {SorterSortingLogFiltersInput} from './graphql/queries/getSorterSortingLogListing';
import {useSorterSortingLogQuery} from './hooks/useSorterSortingLogQuery';
import SorterSortingLogFilter from './components/SorterSortingLogFilter';
import SorterSortingLogColumns from './components/SorterSortingLogColumns';

const SorterSortingLogPage: React.FC = () => {
    const {t} = useTranslation();
    const {currentWarehouse} = useWarehouseContext();

    // 与 WmsParcelsPage 保持一致：用 useMemo 只计算一次，dayjs() 使用设备本地时区
    const defaultCreatedAtRange = useMemo(() => ({
        createdAtStart: dayjs().subtract(7, 'day').unix(),
        createdAtEnd: dayjs().unix(),
    }), []);

    const [tempFilters, setTempFilters] = useState<SorterSortingLogFiltersInput>(defaultCreatedAtRange);
    const [filters, setFilters] = useState<SorterSortingLogFiltersInput>(defaultCreatedAtRange);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    const initializedRef = useRef(false);

    // warehouseId 不写入 state，在 query 时注入，与 WmsParcelsPage 保持一致
    // 用 useMemo 稳定引用，避免每次 render 产生新对象导致 Apollo 无限重请求
    const effectiveFilters = useMemo<SorterSortingLogFiltersInput>(() => ({
        ...filters,
        warehouseId: currentWarehouse?.id ? Number(currentWarehouse.id) : undefined,
    }), [filters, currentWarehouse?.id]);

    const {data, loading, totalElements, refetch} = useSorterSortingLogQuery(effectiveFilters, currentPage, pageSize);

    useEffect(() => {
        if (!initializedRef.current) {
            initializedRef.current = true;
        } else {
            setTempFilters(defaultCreatedAtRange);
            setFilters(defaultCreatedAtRange);
            setCurrentPage(1);
        }
    }, [currentWarehouse?.id]);

    const buildQueryVariables = (f: SorterSortingLogFiltersInput, page: number) => ({
        filters: {
            ...f,
            warehouseId: currentWarehouse?.id ? Number(currentWarehouse.id) : undefined,
        },
        pageRequest: {page, size: pageSize},
    });

    const handleFilter = () => {
        setFilters(tempFilters);
        setCurrentPage(1);
        refetch(buildQueryVariables(tempFilters, 1));
    };

    const handleResetFilters = () => {
        setTempFilters(defaultCreatedAtRange);
        setFilters(defaultCreatedAtRange);
        setCurrentPage(1);
        refetch(buildQueryVariables(defaultCreatedAtRange, 1));
    };

    const handlePageChange = (page: number, size: number) => {
        setCurrentPage(page);
        setPageSize(size);
    };

    return (
        <div className="bg-white rounded-lg shadow-sm p-6">
            <SorterSortingLogFilter
                filters={tempFilters}
                setFilters={setTempFilters}
                handleFilter={handleFilter}
                handleResetFilters={handleResetFilters}
            />

            <SorterSortingLogColumns data={data} loading={loading} />

            <div className="flex justify-between items-center mt-4">
                <div className="text-gray-500 text-sm">
                    {t('wms.sorterSortingLog.pagination.total', {total: totalElements})}
                </div>
                <Pagination
                    current={currentPage}
                    total={totalElements}
                    pageSize={pageSize}
                    size="small"
                    showSizeChanger
                    onChange={handlePageChange}
                />
            </div>
        </div>
    );
};

export default SorterSortingLogPage;
